import Pagination from "../../../components/pagination/Pagination";
import TableComponent from "../../../components/table/TableComponent";
import { DataElement, Tabs } from "../../../components/tabs/Tabs";
import { Button, Dropdown, Input, Option } from "@fluentui/react-components";
import { TABLE } from "../../../utils/Enums";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ConnectService,
  KS_customerservice_Service_Clients_Connect_CompanyAdminDto as ConnectAccountedCompanyDTO,
  KS_customerservice_Service_Clients_Connect_AdminTenantDto as ConnectSubscriptionDataDTO,
} from "../../../CustomerSvcService";
import DialogComponent from "../../../components/dialog/DialogComponent";
import { useDebounce } from "../../../hooks/useDebouncer";

const styles = `
main {
  padding: 20px;
}
.deleteBtn {
  background-color: #d32f2f;
  color: white;
}
.deleteBtn:hover {
  background-color: #f13232;
  color: white;
}
`;

type ActionType = "delete" | "inactivate" | "activate";

interface Props {}

const createTabContent = (): DataElement[] => [
  { label: "Könyvelt cégek" },
  { label: "Előfizetői adatok" },
];

export const ConnectAdminPage: React.FC<Props> = () => {
  const connectAdminPageTabContent = {
    0: {
      title: "könyvelt cégek",
      dataTableType: TABLE.CONNECT_ACCOUNTED_COMPANIES,
    },
    1: {
      title: "előfizetői adatok",
      dataTableType: TABLE.CONNECT_SUBSCRIPTION_DATA,
    },
  };
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [data, setData] = useState<
    null | ConnectAccountedCompanyDTO[] | ConnectSubscriptionDataDTO[]
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [confirmationModalType, setConfirmationModalType] = useState<
    ActionType | undefined
  >(undefined);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search, 2000);
  const selectedCompanyRef = useRef<ConnectAccountedCompanyDTO | undefined>(
    undefined,
  );

  const navigateBack = () => {
    navigate(-1);
  };
  const BASIC_PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(undefined as number | undefined);
  const [pageSize, setPageSize] = useState(`${BASIC_PAGE_SIZE}`);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOngoing, setOngoing] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const handleRowAction = async (
    action: string | null | undefined,
    id?: any,
  ): Promise<void> => {
    if (!action || !data) {
      return;
    }

    setConfirmationModalOpen(true);
    setConfirmationModalType(action as ActionType | undefined);
    selectedCompanyRef.current = (data as ConnectAccountedCompanyDTO[])?.find(
      (element: ConnectAccountedCompanyDTO) => element.id === id,
    );
  };

  const handleServiceAction = async (
    type: ActionType,
    id?: string,
    tenantId?: string,
  ) => {
    try {
      if (!tenantId || !id) {
        return;
      }
      setOngoing(true);
      if (type === "delete") {
        await ConnectService.deleteApiConnectCompanies(id);
      } else if (type === "inactivate") {
        await ConnectService.patchApiConnectCompaniesInactivate(id, tenantId);
      } else if ((type = "activate")) {
        await ConnectService.patchApiConnectCompaniesActivate(id, tenantId);
      }

      const responseGet = await ConnectService.getApiConnectCompanies();
      if (responseGet) {
        setError(undefined);
        setData(responseGet ?? []);
      }
    } catch (e) {
      const highlightedErrors: Record<string, string> = {
        "Company limit exceeded.": "Betelt a cég limit",
        "Company not found.": "Nem található a cég",
        "Company.CompanyStatusUnchanged":
          "Nem sikerült megváltoztatni a cég státuszát",
        "Company is already in the requested status.":
          "Már kérvényezve lett ez a státusz a cég számára",
      };
      const errorMessage = e ? `${e}`.split("ApiError: ")[1] : "";

      if (errorMessage in highlightedErrors) {
        setError(highlightedErrors[errorMessage]);
      } else {
        setError("Probléma történt");
      }
    } finally {
      setConfirmationModalOpen(false);
      setConfirmationModalType(undefined);
      setOngoing(false);
    }
  };

  const getData = async (searchValue?: string) => {
    setIsLoading(true);
    try {
      const skipValue = (currentPage - 1) * +pageSize;
      let res: ConnectAccountedCompanyDTO[] = [];
      if (activeTab === 0) {
        res = await ConnectService.getApiConnectCompanies(
          undefined,
          undefined,
          undefined,
          undefined,
          searchValue,
          undefined,
          undefined,
          skipValue,
          +pageSize,
        );
        //setTotalCount(res.totalCount ?? 0);
      } else {
        const regex = new RegExp(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        );
        res = await ConnectService.getApiConnectTenants(
          undefined,
          undefined,
          searchValue && (searchValue ?? "").length > 0
            ? regex.test(searchValue)
              ? `(id eq ${searchValue})`
              : `contains(tolower(name), '${searchValue?.toLowerCase()}')`
            : undefined,
          pageSize,
          `${skipValue}`,
        );

        //setTotalCount(res.totalCount ?? 0);
      }
      if (res) {
        setData(res ?? []);
      }
    } catch (e) {
      console.error("Hiba az adatok lekérésekor:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setError(undefined);
    getData(search);
  }, [activeTab, debounceValue, currentPage]);

  useEffect(() => {
    setSearch("");
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <main>
      <style>{styles}</style>
      <Tabs data={createTabContent()} activateTab={setActiveTab} />
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Button onClick={navigateBack}>Vissza</Button>
            <Dropdown
              defaultValue={`${BASIC_PAGE_SIZE}`}
              style={{ minWidth: "80px" }}
              onOptionSelect={(_, data) =>
                setPageSize(data.optionValue as string)
              }
            >
              <Option value="10">10</Option>
              <Option value="20">20</Option>
              <Option value="50">50</Option>
              <Option value="100">100</Option>
            </Dropdown>
            <span style={{ fontSize: "14px" }}>találat oldalaként</span>
          </div>
          <h1
            style={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "40px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Connect - {connectAdminPageTabContent[activeTab].title}
          </h1>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Input
              type="text"
              placeholder="Keresés..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "200px" }}
            />
          </div>
        </div>
        {error && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ color: "red", fontSize: 16, padding: 4 }}>
              {error}
            </span>
          </div>
        )}
      </div>
      <>
        <Pagination
          currentPage={currentPage}
          totalItems={totalCount}
          pageSize={Number(pageSize)}
          onPageChange={(p) => setCurrentPage(p)}
          canStepForward={data ? (data ?? []).length > 0 : false}
        />
        <TableComponent
          type={connectAdminPageTabContent[activeTab].dataTableType}
          handleRowAction={handleRowAction}
          data={data}
          isLoading={isLoading ?? false}
          pageSize={Number(pageSize)}
          isServerSidePagination={false}
        />
        <DialogComponent
          open={isConfirmationModalOpen}
          setOpen={setConfirmationModalOpen}
          handleSave={() =>
            confirmationModalType !== undefined &&
            handleServiceAction(
              confirmationModalType,
              selectedCompanyRef.current?.id ?? undefined,
              selectedCompanyRef.current?.tenantId ?? undefined,
            )
          }
          handleClose={() => {
            setConfirmationModalOpen(false);
            selectedCompanyRef.current = undefined;
          }}
          okBtnText={
            confirmationModalType === "delete"
              ? "Törlés"
              : confirmationModalType === "inactivate"
                ? "Inaktiválás"
                : "Aktiválás"
          }
          closeBtnText="Mégse"
          title={
            confirmationModalType === "delete"
              ? "Cég eltávolítása"
              : confirmationModalType === "inactivate"
                ? "Cég inaktiválása"
                : "Cég aktiválása"
          }
          isModalLoading={isOngoing}
        >
          <div style={{ marginBottom: "10px" }}>
            <div className={""}>
              <span style={{ fontWeight: 700, marginRight: 5 }}>
                {selectedCompanyRef.current?.name ?? ""}
              </span>
              <span>cég</span>
              <div style={{ marginTop: 10 }}>
                {confirmationModalType === "delete"
                  ? "Biztos vagy benne, hogy törölni szeretnéd a következő céget?"
                  : confirmationModalType === "inactivate"
                    ? "Biztos inaktiválod következő céget?"
                    : "Biztos aktiválod következő céget?"}
              </div>
            </div>
          </div>
        </DialogComponent>
      </>
    </main>
  );
};
